import { Capacitor } from "@capacitor/core";
import type { UXCamConfiguration } from "cordova-uxcam";
import { UXCamConfig } from "../constants/UXCamConfig";

/**
 * cordova-uxcam 3.8.x registers as a Capacitor plugin exposed at
 * `Capacitor.Plugins.UXCam` — it does NOT set the legacy `window.UXCam` global
 * the older integration relied on. So all UXCam access goes through Capacitor.
 * The plugin is only present on native builds; on web/Cypress it's undefined
 * and every helper here is a no-op.
 */
interface UXCamPlugin {
    optIntoSchematicRecordings(): Promise<void>;
    startWithConfiguration(options: {
        configuration: UXCamConfiguration;
    }): Promise<{ sessionUrl: string }>;
    setUserIdentity(options: { identity: string }): Promise<void>;
    tagScreenName(options: { screenName: string }): Promise<void>;
}

function getUXCam(): UXCamPlugin | undefined {
    return (Capacitor as any).Plugins?.UXCam;
}

/**
 * The in-flight/settled startWithConfiguration call. Kept at module scope so a
 * second startUXCam() (e.g. the app-open start followed by a post-login
 * identify) reuses the same session instead of spinning up a duplicate, and so
 * identifyUXCamUser can sequence the identity after the session exists. Reset
 * on failure so a later call can retry the start.
 */
let startPromise: Promise<void> | undefined;

/**
 * Start UXCam recording without a user identity.
 *
 * REFSIX-776: called on first app open, before the user has registered or
 * logged in, so session replay captures pre-registration behaviour and
 * drop-off. Idempotent, and a no-op when the native plugin isn't present
 * (web / Cypress), so it's safe to call unconditionally. Returns the start
 * promise so callers can sequence work after the session exists.
 */
export function startUXCam(): Promise<void> {
    const uxcam = getUXCam();
    if (!uxcam) return Promise.resolve();
    if (!startPromise) {
        uxcam
            .optIntoSchematicRecordings()
            .catch((err) => console.warn("UXCam optIn failed", err));
        startPromise = uxcam
            .startWithConfiguration({ configuration: UXCamConfig })
            .then(() => undefined)
            .catch((err) => {
                console.warn("UXCam start failed", err);
                startPromise = undefined;
            });
    }
    return startPromise;
}

/**
 * Associate the running UXCam session with a username. Ensures recording has
 * been started first (defensive) and only attaches the identity once the
 * session start has settled, so the identity can't race a first-ever start
 * and be dropped.
 */
export function identifyUXCamUser(username: string): void {
    const uxcam = getUXCam();
    if (!uxcam) return;
    startUXCam().then(() =>
        uxcam
            .setUserIdentity({ identity: username })
            .catch((err) => console.warn("UXCam identify failed", err))
    );
}

export function uxCamTagScreen(screenName: string): void {
    const uxcam = getUXCam();
    if (!uxcam) return;
    uxcam
        .tagScreenName({ screenName })
        .catch((err) => console.warn("UXCam tagScreen failed", err));
}
